/**
 * Assistant API client — typed functions for the proactive-assistant endpoints
 * (prefs, delivery channels, Telegram linking, briefing preview).
 *
 * Field names are snake_case on purpose: they mirror the backend JSON struct
 * tags exactly (repo convention — see CLAUDE.md "API & schema discipline").
 */
import { apiClient } from './index';

/**
 * Which optional channels this deployment can deliver to. Rides ONLY on
 * the GET /assistant/prefs response (backend `prefsResponse`); the PATCH
 * echo never carries it. Task 17 hides the Telegram card when
 * `capabilities.telegram` is false.
 */
export interface AssistantCapabilities {
  telegram: boolean;
  /**
   * Calendar providers (Plan C). False => the server mounted no routes for
   * that provider (no client credentials configured), so the UI must hide its
   * card entirely rather than offer a button that 404s.
   */
  google_calendar: boolean;
  icloud_calendar: boolean;
}

export interface AssistantPrefs {
  user_id: string;
  tenant_id: string;
  enabled: boolean;
  timezone: string; // IANA, e.g. "Africa/Nairobi"
  morning_time: string | null; // "HH:MM", null = slot disabled
  evening_time: string | null; // "HH:MM", null = slot disabled
  urgent_alerts: boolean;
  quiet_hours_start: string | null; // "HH:MM"
  quiet_hours_end: string | null; // "HH:MM"
  updated_at: string; // RFC3339
  capabilities: AssistantCapabilities; // GET only — see AssistantPrefsBase
}

/**
 * The PATCH /assistant/prefs echo: bare prefs, NO capabilities key
 * (backend PatchPrefs responds with the Prefs struct directly). Callers
 * must keep the capabilities value from GET and never replace it from a
 * PATCH response — the narrower return type of updateAssistantPrefs
 * enforces that at compile time.
 */
export type AssistantPrefsBase = Omit<AssistantPrefs, 'capabilities'>;

/**
 * Writable subset for PATCH /assistant/prefs (server owns the rest).
 *
 * Hand-written on purpose — NOT Partial<Pick<AssistantPrefs, ...>>. The
 * read type's time fields are `string | null`, but the wire convention
 * for the PATCH body is: field absent = unchanged, "" = clear the slot
 * to NULL (disabled), "HH:MM" = set. `null` is never sent, so every
 * time field here is a plain `string`.
 */
export interface AssistantPrefsPatch {
  enabled?: boolean;
  timezone?: string; // IANA name
  morning_time?: string; // "" clears, "HH:MM" sets
  evening_time?: string; // "" clears, "HH:MM" sets
  urgent_alerts?: boolean;
  quiet_hours_start?: string; // "" clears, "HH:MM" sets
  quiet_hours_end?: string; // "" clears, "HH:MM" sets
}

export interface AssistantChannel {
  id: string;
  tenant_id: string;
  user_id: string;
  kind: string; // email | telegram | whatsapp | sms
  address_masked: string; // e.g. "g•••@oneplusglobal.net" — never the raw address
  verified_at: string | null; // always present (backend tag has no omitempty); null = unverified, RFC3339 otherwise
  receive_briefings: boolean;
  receive_urgent: boolean;
}

/** Writable subset for PATCH /assistant/channels/{id}. */
export interface AssistantChannelPatch {
  receive_briefings?: boolean;
  receive_urgent?: boolean;
}

export interface TelegramLinkResponse {
  code: string; // one-time code, 15-min expiry
  deep_link: string; // https://t.me/<bot>?start=<code>
}

export interface BriefingPreview {
  subject: string;
  html: string;
  narrative_source: 'llm' | 'fallback';
}

// ─── Prefs ────────────────────────────────────────────────────────────────

export async function getAssistantPrefs(): Promise<AssistantPrefs> {
  const { data } = await apiClient.get<AssistantPrefs>('/assistant/prefs');
  return data;
}

/**
 * PATCH returns the bare prefs echo — no capabilities. Merge it over
 * local state but keep the capabilities object from getAssistantPrefs.
 */
export async function updateAssistantPrefs(
  patch: AssistantPrefsPatch,
): Promise<AssistantPrefsBase> {
  const { data } = await apiClient.patch<AssistantPrefsBase>(
    '/assistant/prefs',
    patch,
  );
  return data;
}

// ─── Channels ─────────────────────────────────────────────────────────────

export async function listAssistantChannels(): Promise<AssistantChannel[]> {
  // The backend returns a BARE JSON array (no {data:[...]} envelope) —
  // fixed contract with Task 5's handler. `?? []` guards a 200 with a
  // JSON `null` body (Go marshals a nil slice as null).
  const { data } = await apiClient.get<AssistantChannel[]>('/assistant/channels');
  return data ?? [];
}

export async function updateAssistantChannel(
  id: string,
  patch: AssistantChannelPatch,
): Promise<void> {
  await apiClient.patch(`/assistant/channels/${id}`, patch);
}

export async function deleteAssistantChannel(id: string): Promise<void> {
  await apiClient.delete(`/assistant/channels/${id}`);
}

export async function linkTelegram(): Promise<TelegramLinkResponse> {
  const { data } = await apiClient.post<TelegramLinkResponse>(
    '/assistant/channels/telegram/link',
  );
  return data;
}

// ─── Preview ──────────────────────────────────────────────────────────────

export async function previewBriefing(): Promise<BriefingPreview> {
  const { data } = await apiClient.get<BriefingPreview>('/assistant/preview');
  return data;
}
